import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "@remix-run/react";
import {
  AlignLeft,
  Calendar,
  Filter,
  Flag,
  ListChecks,
  Settings,
} from "lucide-react";
import { MOBILE_HEADER_SCROLL_TRANSITION_CLASS } from "~/ui-apps/design-system/constants/mobileSignedInHeader";
import { useMobileNavScrollVisibility } from "~/ui-apps/hooks/useMobileNavScrollVisibility";
import { isFinancialGlanceTickerPath } from "~/utilities/tickerRoutePaths.util";

const SIGNED_IN_HOME = "/home";

const isMoreMenuRoute = (pathname: string) =>
  pathname === "/evaluation-criteria" ||
  pathname.startsWith("/evaluation-criteria/") ||
  pathname === "/my-screens" ||
  pathname.startsWith("/my-screens/") ||
  pathname === "/earnings-calendar" ||
  pathname === "/settings" ||
  pathname === "/report-issue";

const MobileSignedInHeader: React.FC = () => {
  const isVisible = useMobileNavScrollVisibility();
  const location = useLocation();
  // Glance scroll is not window scroll. Root collapses this bar in-flow from the nested scrollport.
  const isTickerDetailPath = isFinancialGlanceTickerPath(location.pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [canPortal, setCanPortal] = useState(false);
  const moreMenuActive = mobileMenuOpen || isMoreMenuRoute(location.pathname);

  useEffect(() => {
    setCanPortal(true);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const moreMenuItemClass = (active: boolean) =>
    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors hover:bg-gray-100 active:bg-gray-100 ${
      active ? "text-emerald-900" : "text-gray-700"
    }`;

  // Portal past ticker-detail's overflow-hidden + transform wrapper, which otherwise
  // traps position:fixed and clips the callout below the header strip.
  const moreMenuLayer =
    canPortal &&
    createPortal(
      <>
        <div
          role="presentation"
          className={`fixed inset-0 z-[80] bg-black/40 backdrop-blur-[1px] transition-opacity duration-200 ease-out lg:hidden ${
            mobileMenuOpen
              ? "opacity-100"
              : "pointer-events-none opacity-0"
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />

        <div
          className={`fixed z-[90] w-[min(14rem,calc(100vw-1.25rem))] origin-top-right lg:hidden
          top-[calc(3.5rem+env(safe-area-inset-top,0px))] right-3 rounded-xl border border-gray-200 bg-white shadow-xl shadow-black/10
          transition-all duration-200 ease-out
          ${
            mobileMenuOpen
              ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
              : "pointer-events-none -translate-y-2 scale-95 opacity-0"
          }`}
          role="menu"
          aria-hidden={!mobileMenuOpen}
        >
          <nav className="p-1.5" aria-label="More navigation">
            <Link
              to="/evaluation-criteria"
              role="menuitem"
              className={moreMenuItemClass(
                location.pathname === "/evaluation-criteria" ||
                  location.pathname.startsWith("/evaluation-criteria/"),
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="flex w-8 justify-center text-gray-400">
                <ListChecks className="h-[1.125rem] w-[1.125rem]" aria-hidden />
              </span>
              Evaluation Criteria
            </Link>
            <div className="mx-2 h-px shrink-0 bg-gray-200" aria-hidden />
            <Link
              to="/my-screens"
              role="menuitem"
              className={moreMenuItemClass(location.pathname === "/my-screens")}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="flex w-8 justify-center text-gray-400">
                <Filter className="h-[1.125rem] w-[1.125rem]" aria-hidden />
              </span>
              My Screens
            </Link>
            <div className="mx-2 h-px shrink-0 bg-gray-200" aria-hidden />
            <Link
              to="/earnings-calendar"
              role="menuitem"
              className={moreMenuItemClass(
                location.pathname === "/earnings-calendar",
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="flex w-8 justify-center text-gray-400">
                <Calendar className="h-[1.125rem] w-[1.125rem]" aria-hidden />
              </span>
              Earnings calendar
            </Link>
            <div className="mx-2 h-px shrink-0 bg-gray-200" aria-hidden />
            <Link
              to="/report-issue"
              role="menuitem"
              className={moreMenuItemClass(location.pathname === "/report-issue")}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="flex w-8 justify-center text-gray-400">
                <Flag className="h-4 w-4" aria-hidden />
              </span>
              Report an issue
            </Link>
            <div className="mx-2 h-px shrink-0 bg-gray-200" aria-hidden />
            <Link
              to="/settings"
              role="menuitem"
              className={moreMenuItemClass(location.pathname === "/settings")}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="flex w-8 justify-center text-gray-400">
                <Settings className="h-[1.125rem] w-[1.125rem]" aria-hidden />
              </span>
              Settings
            </Link>
          </nav>
        </div>
      </>,
      document.body,
    );

  return (
    <>
      <header
        className={
          isTickerDetailPath
            ? "bg-[#faf8f4]"
            : `block lg:hidden fixed inset-x-0 z-30 top-0 bg-background transition-transform ${MOBILE_HEADER_SCROLL_TRANSITION_CLASS} ${
                isVisible ? "translate-y-0" : "-translate-y-full"
              }`
        }
      >
        <nav
          aria-label="Global"
          className="grid h-14 grid-cols-3 items-center gap-1.5 px-1.5 py-3"
        >
          <div aria-hidden="true" />

          <Link
            to={SIGNED_IN_HOME}
            prefetch="intent"
            className="justify-self-center shrink-0"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-black ring-1 ring-black/10">
              <span className="text-[10px] font-bold tracking-tight text-white">
                FYI
              </span>
            </div>
          </Link>

          <button
            type="button"
            aria-expanded={mobileMenuOpen}
            aria-haspopup="true"
            aria-label={mobileMenuOpen ? "Close menu" : "More options"}
            onClick={() => setMobileMenuOpen((open) => !open)}
            className={`relative mr-2.5 flex h-8 w-8 shrink-0 items-center justify-center justify-self-end rounded-full bg-white ring-1 ring-slate-300/70 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/60 ${
              moreMenuActive
                ? "text-emerald-900 ring-emerald-800/25"
                : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <AlignLeft
              className="h-[1.125rem] w-[1.125rem]"
              strokeWidth={moreMenuActive ? 2.75 : 2.5}
              aria-hidden
            />
          </button>
        </nav>
      </header>

      {moreMenuLayer}
    </>
  );
};

export default MobileSignedInHeader;
